import { motion } from "framer-motion";
import { ArrowDownRight, HeartPulse } from "lucide-react";
import { LogoUniverse } from "@/components/site/LogoUniverse";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="line-mask">
    <motion.span
      custom={i}
      variants={lineVariant}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-3 mb-6 border border-brand-black/10 bg-white/85 backdrop-blur px-4 py-2"
        >
          <HeartPulse size={16} className="text-brand-red" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-black">
            Consulenza · FGAS · Bandi · Sicurezza
          </span>
        </motion.div>

        <h1 className="font-heading font-black uppercase leading-[0.94] tracking-tighter text-brand-black text-[8.5vw] sm:text-5xl lg:text-[4.1rem]">
          <Line i={0}>Formazione che</Line>
          <Line i={1}>
            crea{" "}
            <span className="relative inline-block">
              esperienza
              <motion.span
                aria-hidden="true"
                className="absolute left-0 -bottom-1 h-[0.12em] w-full rounded-full bg-brand-red origin-left"
                animate={{ scaleX: [0.15, 1, 0.35, 1], opacity: [0.35, 1, 0.75, 1] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              />
            </span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.35 }}
          className="relative mx-auto mt-7 w-[min(94vw,900px)] h-[clamp(250px,38vw,540px)] bg-transparent overflow-hidden"
          data-testid="hero-universe-box"
        >
          <LogoUniverse />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mx-auto mt-8 text-brand-ink text-base md:text-lg max-w-2xl leading-relaxed"
        >
          Servizi integrati di consulenza, formazione e certificazione per aziende e professionisti, a Frosinone e in tutta Italia.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-8 flex flex-wrap gap-4 justify-center"
        >
          <button
            data-testid="hero-cta-courses"
            onClick={() => scrollTo("#corsi")}
            className="group inline-flex items-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-brand-black transition-colors"
          >
            Scopri i corsi
            <ArrowDownRight size={18} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollTo("#contatti")}
            className="inline-flex items-center gap-2 font-bold uppercase tracking-widest text-sm px-8 py-4 border border-brand-black bg-white text-brand-black hover:bg-brand-black hover:text-white transition-colors"
          >
            Contattaci
          </button>
        </motion.div>
      </div>
    </section>
  );
};
